import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Skeleton,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import DesktopWindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ViewQuiltRoundedIcon from "@mui/icons-material/ViewQuiltRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import { useParams } from "react-router-dom";
import { settingsApi } from "../../api/settings";
import { landingAssetsApi } from "../../api/landingAssets";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import LandingCanvas from "../../components/dashboard/landingEditor/LandingCanvas";
import LandingSettingsDialog from "../../components/dashboard/landingEditor/LandingSettingsDialog";
import { BRAND, emptySettings, mergeSettings } from "../../components/dashboard/landingEditor/config";
import {
  getValueAtPath,
  setValueAtPath,
  settingsPayload,
} from "../../components/dashboard/landingEditor/editorUtils";
import { assetValueFromResponse, isManagedLandingAsset } from "../../utils/landingAssets";

const visualSections = [
  { id: "navbar", label: "Navbar" },
  { id: "hero", label: "Hero" },
  { id: "spotlight", label: "Spotlight" },
  { id: "featured", label: "Destacados" },
  { id: "brands", label: "Marcas" },
  { id: "categories", label: "Categorías" },
  { id: "about", label: "Nosotros" },
  { id: "highlights", label: "Diferenciales" },
  { id: "contact", label: "Contacto" },
  { id: "footer", label: "Footer" },
  { id: "floatingWhatsapp", label: "WhatsApp" },
];

export default function ProjectSitePage() {
  const { projectId } = useParams();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [form, setForm] = React.useState(emptySettings);
  const [activeSection, setActiveSection] = React.useState("navbar");
  const [device, setDevice] = React.useState("desktop");
  const [loading, setLoading] = React.useState(true);
  const [savingPath, setSavingPath] = React.useState("");
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const previewContainerRef = React.useRef(null);

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId) || activeProject,
    [projects, projectId, activeProject]
  );
  const effectiveProject = project || contextProject;
  const landingEnabled = isProjectModuleEnabled(effectiveProject, "landing");
  const productsEnabled = isProjectModuleEnabled(effectiveProject, "products");
  const saving = Boolean(savingPath);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let active = true;
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!active) return;
        setProject(data.project || null);
        if (data.project) setActiveProject(data.project);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [projectId, setActiveProject]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await settingsApi.get(projectId);
      setForm(mergeSettings(data.settings));
    } catch (err) {
      setError(err.message || "No se pudo cargar la configuración de la landing");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const selectSection = React.useCallback((sectionId) => {
    setActiveSection(sectionId);
    window.requestAnimationFrame(() => {
      const container = previewContainerRef.current;
      const target = container?.querySelector(`[data-landing-section="${sectionId}"]`);
      if (!target || !container) return;
      container.scrollTo({ top: Math.max(0, target.offsetTop - 12), behavior: "smooth" });
    });
  }, []);

  const persist = React.useCallback(
    async (nextForm, key = "landing") => {
      try {
        setSavingPath(key);
        setError("");
        const data = await settingsApi.update(projectId, settingsPayload(nextForm, productsEnabled));
        const merged = mergeSettings(data.settings);
        setForm(merged);
        setMessage("Cambio guardado en la Landing.");
        return merged;
      } catch (err) {
        setError(err.message || "No se pudo guardar el cambio");
        throw err;
      } finally {
        setSavingPath("");
      }
    },
    [projectId, productsEnabled]
  );

  const commitValue = React.useCallback(
    async (path, value) => {
      const nextForm = setValueAtPath(form, path, value);
      await persist(nextForm, path);
    },
    [form, persist]
  );

  const commitValues = React.useCallback(
    async (changes) => {
      const entries = Object.entries(changes || {});
      if (!entries.length) return;
      const nextForm = entries.reduce(
        (current, [path, value]) => setValueAtPath(current, path, value),
        form
      );
      await persist(nextForm, entries.map(([path]) => path).join(","));
    },
    [form, persist]
  );

  const uploadAssetFile = React.useCallback(
    async (file, metadata = {}) => {
      const response = await landingAssetsApi.upload(projectId, file, {
        pageKey: "home",
        ...metadata,
      });
      const value = assetValueFromResponse(response);
      if (!value) throw new Error("El backend no devolvió la ruta del archivo");
      return { ...response.asset, value };
    },
    [projectId]
  );

  const deleteAssetFile = React.useCallback(
    async (path) => {
      if (!isManagedLandingAsset(path)) return { ok: true, removed: false };
      return landingAssetsApi.remove(projectId, path);
    },
    [projectId]
  );

  const uploadImageAndCommit = React.useCallback(
    async (path, file, metadata = {}) => {
      setSavingPath(path);
      let uploaded = null;
      try {
        const previousValue = getValueAtPath(form, path, "");
        uploaded = await uploadAssetFile(file, metadata);
        const nextForm = setValueAtPath(form, path, uploaded.value);

        try {
          await persist(nextForm, path);
        } catch (error) {
          await deleteAssetFile(uploaded.value).catch(() => {});
          throw error;
        }

        if (previousValue && previousValue !== uploaded.value && isManagedLandingAsset(previousValue)) {
          await deleteAssetFile(previousValue).catch(() => {
            setMessage("La imagen nueva quedó guardada, pero el archivo anterior no pudo eliminarse.");
          });
        }

        return uploaded.value;
      } finally {
        setSavingPath("");
      }
    },
    [deleteAssetFile, form, persist, uploadAssetFile]
  );

  const removeImageAndCommit = React.useCallback(
    async (path) => {
      const previousValue = getValueAtPath(form, path, "");
      if (!previousValue) return;

      const nextForm = setValueAtPath(form, path, "");
      await persist(nextForm, path);

      if (isManagedLandingAsset(previousValue)) {
        await deleteAssetFile(previousValue).catch(() => {
          setMessage("La imagen se quitó de la landing, pero el archivo físico no pudo eliminarse.");
        });
      }
    },
    [deleteAssetFile, form, persist]
  );

  const saveSettings = async (nextForm) => persist(nextForm, "settings");

  if (effectiveProject && !landingEnabled && !loading) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Landing"
        description="La presencia web básica todavía no está habilitada para este proyecto."
      />
    );
  }

  return (
    <Stack spacing={2.2}>
      <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <ViewQuiltRoundedIcon sx={{ color: BRAND.orange, fontSize: 32 }} />
            <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark, letterSpacing: "-.035em" }}>
              Editor visual de Landing
            </Typography>
          </Stack>
          <Typography sx={{ color: "rgba(25,36,47,.65)", mt: 0.5, maxWidth: 820 }}>
            La maqueta reproduce la landing pública real. Editá textos e imágenes dentro de cada bloque, cambiá estilos de navbar y footer, o activá y ocultá secciones sin salir del visualizador.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            label={
              form.publicStatus === "published"
                ? "Publicada"
                : form.publicStatus === "maintenance"
                  ? "Mantenimiento"
                  : "Borrador"
            }
            color={form.publicStatus === "published" ? "success" : "default"}
            sx={{ fontWeight: 900 }}
          />
          <Chip
            icon={saving ? undefined : <CloudDoneRoundedIcon />}
            label={saving ? "Guardando cambio..." : `Versión ${form.revision || 1}`}
            variant="outlined"
            color={saving ? "warning" : "default"}
            sx={{ fontWeight: 850 }}
          />
          <Button
            type="button"
            startIcon={<SettingsRoundedIcon />}
            variant="outlined"
            disabled={loading || saving}
            onClick={() => setSettingsOpen(true)}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 950, color: BRAND.dark, borderColor: "rgba(25,36,47,.20)" }}
          >
            Ajustes
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Stack spacing={1.2}>
          <Skeleton variant="rounded" height={70} />
          <Skeleton variant="rounded" height={640} />
        </Stack>
      ) : null}
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}

      {!loading ? (
        <>
          <Card
            elevation={0}
            sx={{
              position: "sticky",
              top: 8,
              zIndex: 30,
              borderRadius: 4,
              border: "1px solid rgba(25,36,47,.10)",
              boxShadow: "0 12px 34px rgba(25,36,47,.09)",
              bgcolor: "rgba(255,255,255,.96)",
              backdropFilter: "blur(14px)",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ px: 1.2, py: 1 }}>
              <Stack direction="row" spacing={0.8} sx={{ overflowX: "auto", scrollbarWidth: "thin", flex: 1 }}>
                {visualSections.map((section) => (
                  <Button
                    type="button"
                    key={section.id}
                    variant={activeSection === section.id ? "contained" : "text"}
                    onClick={() => selectSection(section.id)}
                    sx={{
                      flex: "0 0 auto",
                      minWidth: "max-content",
                      borderRadius: 999,
                      px: 2,
                      color: activeSection === section.id ? "#fff" : BRAND.dark,
                      bgcolor: activeSection === section.id ? BRAND.dark : "transparent",
                      textTransform: "none",
                      fontWeight: 900,
                      boxShadow: "none",
                      "&:hover": { bgcolor: activeSection === section.id ? BRAND.dark : "rgba(25,36,47,.06)", boxShadow: "none" },
                    }}
                  >
                    {section.label}
                  </Button>
                ))}
              </Stack>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={device}
                onChange={(_, value) => value && setDevice(value)}
                sx={{ flex: "0 0 auto" }}
              >
                <ToggleButton value="desktop" aria-label="Vista desktop"><DesktopWindowsRoundedIcon fontSize="small" /></ToggleButton>
                <ToggleButton value="mobile" aria-label="Vista mobile"><PhoneIphoneRoundedIcon fontSize="small" /></ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Card>

          <Card
            elevation={0}
            sx={{
              p: { xs: 1, md: 2.2 },
              borderRadius: 4,
              border: "1px solid rgba(25,36,47,.08)",
              bgcolor: "#e9edf0",
            }}
          >
            <Box
              ref={previewContainerRef}
              sx={{
                maxHeight: "calc(100vh - 220px)",
                minHeight: 650,
                overflow: "auto",
                px: { xs: 0, md: 1 },
                py: 1,
                scrollBehavior: "smooth",
              }}
            >
              <LandingCanvas
                form={form}
                activeSection={activeSection}
                device={device}
                productsEnabled={productsEnabled}
                onSelectSection={selectSection}
                onCommit={commitValue}
                onCommitMany={commitValues}
                onUploadImage={uploadImageAndCommit}
                onRemoveImage={removeImageAndCommit}
                saving={saving}
              />
            </Box>
          </Card>

          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.55)", pb: 1 }}>
            La vista usa la misma estructura y los mismos campos que la página genérica. Los productos son muestras visuales; en la web pública se reemplazan por los productos reales del cliente.
          </Typography>
        </>
      ) : null}

      <LandingSettingsDialog
        open={settingsOpen}
        form={form}
        productsEnabled={productsEnabled}
        saving={savingPath === "settings"}
        onClose={() => setSettingsOpen(false)}
        onSave={saveSettings}
        onUploadAsset={uploadAssetFile}
        onDeleteAsset={deleteAssetFile}
      />

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={2200}
        onClose={() => setMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setMessage("")} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
